import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Peep";

const STATE = {
  verified: ["Connected & verified", "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"],
  configured: ["Configured (needs real-world test)", "bg-sky-500/15 text-sky-200 border-sky-400/30"],
  failing: ["Configured — connection failing", "bg-red-500/15 text-red-300 border-red-400/30"],
  missing: ["Missing credentials", "bg-amber-500/15 text-amber-200 border-amber-400/30"],
  unavailable: ["Service temporarily unavailable", "bg-orange-500/15 text-orange-200 border-orange-400/30"],
  awaiting_approval: ["Awaiting external approval", "bg-white/10 text-[#c7c1cf] border-white/15"],
};
const fmtWhen = (v) => (v ? new Date(v).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");
const NAMES = { mongodb: "Database (MongoDB)", supabase: "Supabase photo storage", cloudflare_stream: "Cloudflare Stream video", getstream: "GetStream live & chat", push: "Push notifications", email: "Transactional email", payments: "Payments (CCBill)" };

export function useIntegrationHealth() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const load = useCallback((refresh = false) => {
    setBusy(true);
    return api.get(`/admin/integrations/health${refresh ? "?refresh=true" : ""}`).then((r) => { setD(r.data); setErr(false); }).catch(() => setErr(true)).finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return { d, busy, err, load };
}

export function IntegrationHealthPanel({ onChecked }) {
  const { d, busy, err, load } = useIntegrationHealth();
  const recheck = () => load(true).then(() => onChecked?.());
  return (
    <Panel title={`Integration health · ${d?.environment || "…"}`} action={<button onClick={recheck} disabled={busy} data-testid="health-recheck" className="flex items-center gap-1 text-xs text-[#f0a8fa]"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> {busy ? "Checking…" : "Re-check now"}</button>}>
      {err && <p data-testid="health-error" className="mb-2 text-sm text-red-300">Could not load integration health. Tap Re-check now to try again.</p>}
      {!d ? (!err && <p className="text-sm text-[#a7a0af]">Checking services…</p>) : (
        <ul data-testid="health-list" className="flex flex-col gap-2">
          {d.services.map((s) => {
            const [label, cls] = STATE[s.state] || [s.state, "border-white/15"];
            return (
              <li key={s.service} data-testid={`health-${s.service}`} className="flex flex-wrap items-start justify-between gap-2 border-t border-white/5 pt-2 first:border-0 first:pt-0">
                <span className="min-w-0"><span className="block text-sm font-semibold">{NAMES[s.service] || s.service}</span><span className="block text-xs text-[#a7a0af]">{s.detail}</span>
                  <span className="block text-[0.65rem] text-[#6f6878]">Checked {fmtWhen(s.checked_at)}{s.last_ok_at ? ` · last OK ${fmtWhen(s.last_ok_at)}` : ""}{s.source ? ` · from ${s.source}` : ""}</span></span>
                <span data-testid={`health-state-${s.service}`} className={`shrink-0 rounded-full border px-2.5 py-1 text-[0.65rem] font-semibold ${cls}`}>{label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

export function IntegrationAlertBanner() {
  const { d } = useIntegrationHealth();
  const bad = (d?.services || []).filter((s) => ["failing", "missing"].includes(s.state) && s.service !== "payments");
  if (!bad.length) return null;
  return (
    <div data-testid="integration-alert-banner" className="flex items-start gap-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-amber-100">
      <AlertTriangle size={18} className="mt-0.5 shrink-0" />
      <span>{bad.map((s) => NAMES[s.service]).join(", ")} {bad.length === 1 ? "needs" : "need"} attention in {d.environment}. <Link to="/admin/integrations" className="underline">Open Integrations</Link></span>
    </div>
  );
}
